import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import TrialBanner from "../components/TrialBanner";
import ExecProjectsTable from "../components/executive/ExecProjectsTable";
import ExecFilters from "../components/executive/ExecFilters";
import { api, errMsg } from "../lib/api";
import { applyExecFilters, describeFilters, DEFAULT_EXEC_FILTERS } from "../lib/execView";
import { exportExecutivePDF } from "../lib/execPdf";
import { useAuth } from "../contexts/AuthContext";
import { Boxes, Briefcase, FolderKanban, UserCog, FileDown } from "lucide-react";
import { toast } from "sonner";

function Stat({ id, label, value, sub, icon: Icon }) {
  return (
    <div className="pc-card p-5" data-testid={`exec-stat-${id}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-blue-300/80"><Icon size={12} /> {label}</div>
      <div className="text-3xl font-bold text-white mt-2" style={{ fontFamily: "Outfit" }} data-testid={`exec-stat-${id}-value`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function PerPm({ rows }) {
  const max = Math.max(1, ...rows.map((r) => r.project_count));
  return (
    <div className="pc-card p-5" data-testid="exec-projects-per-pm">
      <div className="text-xs uppercase tracking-widest text-blue-300/80 mb-3">Projects per PM</div>
      {rows.length === 0 && <div className="text-sm text-slate-500">No projects yet.</div>}
      <ul className="space-y-2.5">
        {rows.map((r) => (
          <li key={r.pm_teammate_id || "unassigned"} data-testid={`exec-pm-${r.pm_teammate_id || "unassigned"}`}>
            <div className="flex justify-between text-sm"><span className={r.pm_teammate_id ? "text-slate-200" : "text-slate-500 italic"}>{r.pm_name}</span>
              <span className="text-slate-400 mono">{r.project_count} <span className="text-[11px]">({r.open_count} open)</span></span></div>
            <div className="h-1.5 mt-1 rounded-full bg-slate-800 overflow-hidden"><div className={`h-full ${r.pm_teammate_id ? "bg-blue-500" : "bg-slate-600"}`} style={{ width: `${(r.project_count / max) * 100}%` }} /></div>
          </li>
        ))}
      </ul>
      <p className="text-[11px] text-slate-500 mt-4">Assign a PM in each project's header.</p>
    </div>
  );
}

export default function ExecutivePage() {
  const { user, setUser } = useAuth();
  const [d, setD] = useState(null);
  const [portfolios, setPortfolios] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [filters, setFilters] = useState({ ...DEFAULT_EXEC_FILTERS, ...(user?.exec_filters || {}) });
  useEffect(() => {
    Promise.all([api.get("/analytics/executive"), api.get("/portfolios"), api.get("/programs")])
      .then(([e, pf, pg]) => { setD(e.data); setPortfolios(pf.data); setPrograms(pg.data); })
      .catch((e) => toast.error(errMsg(e)));
  }, []);

  const pms = useMemo(() => {
    const m = new Map();
    (d?.projects || []).forEach((p) => p.pm_teammate_id && m.set(p.pm_teammate_id, p.pm_name || "Unknown"));
    return [...m].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [d]);
  const view = useMemo(() => (d ? applyExecFilters(d, portfolios, filters) : null), [d, portfolios, filters]);

  const changeFilters = async (next) => {
    setFilters(next);
    setUser((u) => ({ ...u, exec_filters: next }));
    try { await api.put("/account/preferences", { exec_filters: next }); }
    catch (e) { console.warn("Failed to save executive filters", e); }
  };
  const exportPdf = () => {
    try {
      exportExecutivePDF({ counts: view.counts, projects: view.projects, perPm: view.perPm, filterText: describeFilters(filters, { portfolios, programs, pms }) });
      toast.success("Executive briefing downloaded");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <AppLayout>
      <TrialBanner />
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white" style={{ fontFamily: "Outfit" }}>Executive overview</h1>
          <p className="text-slate-400 mt-1">Every portfolio, program and project at a glance.</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/app" className="text-sm text-blue-400 hover:text-blue-300" data-testid="exec-all-projects-link">All projects →</Link>
          <button onClick={exportPdf} disabled={!view} data-testid="exec-export-pdf-btn"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-lg shadow-blue-900/30 disabled:opacity-50">
            <FileDown size={14} /> Export PDF briefing
          </button>
        </div>
      </div>
      {!view ? <div className="text-slate-400" data-testid="exec-loading">Loading…</div> : (
        <>
          <ExecFilters filters={filters} onChange={changeFilters} portfolios={portfolios} programs={programs} pms={pms} />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <Stat id="portfolios" label="Portfolios" value={view.counts.portfolio_count} icon={Boxes} />
            <Stat id="programs" label="Programs" value={view.counts.program_count} icon={Briefcase} />
            <Stat id="projects" label="Projects" value={view.counts.project_count} sub={`${view.counts.open_project_count} open`} icon={FolderKanban} />
            <Stat id="pms" label="Project managers" value={view.counts.pm_count} icon={UserCog} />
          </div>
          <div className="grid lg:grid-cols-[1fr_300px] gap-4 items-start">
            <ExecProjectsTable projects={view.projects} />
            <PerPm rows={view.perPm} />
          </div>
        </>
      )}
    </AppLayout>
  );
}
